/**
 * @file entitlements.js
 * @description The one rule for "is this agency Pro", mirrored from the database function
 *   agency_is_pro (and, for the tier, agency_plan_tier) (migration 20261022090000). An agency is Pro when its plan is pro (or enterprise)
 *   OR its Founder access is active: is_founder and not past founder_expires_at (no expiry means an
 *   open ended grant). A Seasonal Pause (hibernate) is not Pro unless Founder access is active.
 *
 *   Slot limits, extra opening prices and Pro only actions are decided on the server from the same
 *   rule (check_posting_slot, agency_entitlements, send_outreach). This helper only drives what the
 *   app shows, so the two can never disagree about who sees Pro features.
 * @module lib/entitlements
 */
import { getCachedConfig } from "./configService";

/**
 * @param {{ plan?: string|null, is_founder?: boolean|null, founder_expires_at?: string|Date|null }} agency
 * @returns {boolean}
 */
export function isFounderActive(agency) {
  if (!agency?.is_founder) return false;
  const exp = agency.founder_expires_at;
  if (!exp) return true;
  const t = (exp instanceof Date ? exp : new Date(exp)).getTime();
  return Number.isFinite(t) && t > Date.now();
}

/**
 * @param {{ plan?: string|null, is_founder?: boolean|null, founder_expires_at?: string|Date|null }} agency
 * @returns {boolean}
 */
export function isProAgency(agency) {
  if (!agency) return false;
  if (isFounderActive(agency)) return true;
  const plan = String(agency.plan || "starter").toLowerCase();
  return plan === "pro" || plan === "enterprise";
}

/**
 * Logins Pro includes, the owner counted (Admin > Config plan_pro_team_logins; the server's _team_seat_limit reads
 * the same key). Starter is always 1. Display only: the server enforces the seat limit.
 * @param {(key: string) => any} getConfig e.g. configService.getCachedConfig, or a lookup into loadAllConfigs()
 * @returns {number}
 */
export function proTeamLogins(getConfig) {
  return Math.max(1, Number(getConfig?.("plan_pro_team_logins")) || 3);
}

/**
 * The plan tier, mirrored from the database function agency_plan_tier (migration 20261123090000), which is the
 * engine and the only enforcement. Display only: pro | starter | promo | paused | none.
 *   pro      plan pro/enterprise, or active Founder
 *   paused   Seasonal Pause (plan hibernate), not Founder
 *   starter  a non-Pro agency with an active (or in-grace) extra-openings subscription of quantity 1 or more: Starter IS
 *            that subscription (quantity N = Starter with its first opening included plus N-1 extra openings)
 *   promo    no paid plan and the free period has not ended
 *   none     no paid plan after the free period: read-only
 * @param {{ plan?: string|null, is_founder?: boolean|null, founder_expires_at?: string|Date|null }} agency
 * @param {{ promoActive?: boolean, hasActiveExtras?: boolean }} [ctx] promoActive from getStarterPromoDates().isPromoActive
 * @returns {"pro"|"starter"|"promo"|"paused"|"none"}
 */
export function planTier(agency, ctx = {}) {
  if (isProAgency(agency)) return "pro";
  const plan = String(agency?.plan || "starter").toLowerCase();
  if (plan === "hibernate") return "paused";
  if (plan === "starter" && ctx.hasActiveExtras) return "starter";
  if (ctx.promoActive) return "promo";
  return "none";
}

/** Paid plan = pro or starter (server: agency_has_paid_plan). */
export function hasPaidPlan(agency, ctx) {
  const t = planTier(agency, ctx);
  return t === "pro" || t === "starter";
}

/** Access = paid plan, promo, or Seasonal Pause (server: agency_has_access). */
export function hasPlanAccess(agency, ctx) {
  return planTier(agency, ctx) !== "none";
}

/**
 * Read-only account (server: agency_entitlements.read_only). Only true when the new plan model is live
 * (platform_config billing_starter_enabled): with the switch off nothing is ever read-only.
 */
export function isReadOnlyAgency(agency, ctx = {}) {
  return Boolean(ctx.billingStarterEnabled) && planTier(agency, ctx) === "none";
}

/**
 * The ONE switch for naming ABAcases Workforce in public pricing copy and plan tables
 * (platform_config workforce_enabled). Lives here, not in configService, so a test that stubs configService
 * with only getCachedConfig keeps working.
 * @returns {boolean}
 */
export function isWorkforceNamed() {
  const v = getCachedConfig("workforce_enabled");
  return v === true || v === "true";
}
